<div class="right-drawer">
  <!-- Header -->
  <div class="right-drawer__header">
    <h2 class="right-drawer__title">{{ drawerTitle() }}</h2>
    <button mat-icon-button
            (click)="close()"
            [attr.aria-label]="closeAriaLabel"
            class="right-drawer__close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="right-drawer__content">
    <!-- Success Message -->
    @if (successMessage()) {
      <div class="right-drawer__message right-drawer__message--success">
        <mat-icon>check_circle</mat-icon>
        <span>{{ successMessage() }}</span>
      </div>
    }

    <!-- Error Message -->
    @if (errorMessage()) {
      <div class="right-drawer__message right-drawer__message--error">
        <mat-icon>error</mat-icon>
        <span>{{ errorMessage() }}</span>
      </div>
    }

    @switch (activeAction()) {
      @case ('createUser') {
        <form [formGroup]="userForm" class="right-drawer__form">
          <!-- Input Mode Toggle -->
          <div class="input-mode-toggle">
            <button type="button"
                    mat-stroked-button
                    [class.active]="userInputMode() === 'name'"
                    (click)="userInputMode() === 'code' && toggleInputMode()">
              <span i18n="@@home.rightDrawer.button.registerByName">氏名で登録</span>
            </button>
            <button type="button"
                    mat-stroked-button
                    [class.active]="userInputMode() === 'code'"
                    (click)="userInputMode() === 'name' && toggleInputMode()">
              <span i18n="@@home.rightDrawer.button.registerByCode">コードで登録</span>
            </button>
          </div>

          @if (userInputMode() === 'name') {
            <!-- Last Name -->
            <mat-form-field appearance="outline" class="form-field--full">
              <mat-label i18n="@@home.rightDrawer.label.lastName">姓</mat-label>
              <input matInput formControlName="lastName">
              @if (userForm.get('lastName')?.hasError('required')) {
                <mat-error i18n="@@home.rightDrawer.error.lastNameRequired">姓を入力してください</mat-error>
              }
            </mat-form-field>

            <!-- First Name -->
            <mat-form-field appearance="outline" class="form-field--full">
              <mat-label i18n="@@home.rightDrawer.label.firstName">名</mat-label>
              <input matInput formControlName="firstName">
              @if (userForm.get('firstName')?.hasError('required')) {
                <mat-error i18n="@@home.rightDrawer.error.firstNameRequired">名を入力してください</mat-error>
              }
            </mat-form-field>
          } @else {
            <!-- Code -->
            <mat-form-field appearance="outline" class="form-field--full">
              <mat-label i18n="@@home.rightDrawer.label.code">コード</mat-label>
              <input matInput formControlName="code">
              @if (userForm.get('code')?.hasError('required')) {
                <mat-error i18n="@@home.rightDrawer.error.codeRequired">コードを入力してください</mat-error>
              }
            </mat-form-field>
          }

          <!-- Birth Date -->
          <mat-form-field appearance="outline" class="form-field--full">
            <mat-label i18n="@@home.rightDrawer.label.birthDate">生年月日</mat-label>
            <input matInput formControlName="birthDate">
            <mat-hint i18n="@@home.rightDrawer.hint.dateFormat">YYYY/MM/DD</mat-hint>
          </mat-form-field>

          <!-- Sex -->
          <div class="form-field-group">
            <label class="form-field-label" i18n="@@home.rightDrawer.label.sex">性別</label>
            <mat-radio-group formControlName="sex" class="sex-radio-group">
              @for (option of sexOptions; track option.value) {
                <mat-radio-button [value]="option.value">
                  {{ option.label }}
                </mat-radio-button>
              }
            </mat-radio-group>
          </div>

          <!-- Contract Start Date -->
          <mat-form-field appearance="outline" class="form-field--full">
            <mat-label i18n="@@home.rightDrawer.label.contractStartDate">利用開始日</mat-label>
            <input matInput formControlName="contractStartAt">
            @if (userForm.get('contractStartAt')?.hasError('required')) {
              <mat-error i18n="@@home.rightDrawer.error.contractStartDateRequired">利用開始日を入力してください</mat-error>
            }
            <mat-hint i18n="@@home.rightDrawer.hint.dateFormat">YYYY/MM/DD</mat-hint>
          </mat-form-field>
        </form>
      }

      @case ('inviteStaff') {
        <form [formGroup]="inviteForm" class="right-drawer__form">
          <!-- Email -->
          <mat-form-field appearance="outline" class="form-field--full">
            <mat-label i18n="@@home.rightDrawer.label.email">メールアドレス</mat-label>
            <input matInput formControlName="email" type="email">
            @if (inviteForm.get('email')?.hasError('required')) {
              <mat-error i18n="@@home.rightDrawer.error.emailRequired">メールアドレスを入力してください</mat-error>
            }
            @if (inviteForm.get('email')?.hasError('email')) {
              <mat-error i18n="@@home.rightDrawer.error.emailInvalid">有効なメールアドレスを入力してください</mat-error>
            }
          </mat-form-field>

          <!-- Roles -->
          <div class="form-field-group">
            <label class="form-field-label" i18n="@@home.rightDrawer.label.permissions">権限</label>
            <div class="checkbox-group">
              <mat-checkbox formControlName="accountManageRole" i18n="@@home.rightDrawer.checkbox.accountManagement">
                アカウント管理
              </mat-checkbox>
              <mat-checkbox formControlName="userManageRole" i18n="@@home.rightDrawer.checkbox.userManagement">
                ユーザー管理
              </mat-checkbox>
            </div>
            <p class="form-hint" i18n="@@home.rightDrawer.hint.autoGrantedPermissions">計測・参照権限は自動で付与されます</p>
          </div>
        </form>
      }

      @default {
        <div class="right-drawer__empty">
          <p i18n="@@home.rightDrawer.message.selectAction">アクションを選択してください</p>
        </div>
      }
    }
  </div>

  <!-- Footer -->
  <div class="right-drawer__footer">
    <button mat-stroked-button
            (click)="close()"
            [disabled]="isSubmitting()"
            i18n="@@home.rightDrawer.button.cancel">
      キャンセル
    </button>
    <button mat-flat-button
            color="primary"
            class="submit-button"
            [class.submitting]="isSubmitting()"
            [disabled]="!isFormValid() || isSubmitting()"
            (click)="submitForm()">
      @if (isSubmitting()) {
        <mat-spinner diameter="20" class="white-spinner"></mat-spinner>
      }
      @if (activeAction() === 'createUser') {
        <span i18n="@@home.rightDrawer.button.create">作成</span>
      } @else if (activeAction() === 'inviteStaff') {
        <span i18n="@@home.rightDrawer.button.invite">招待</span>
      }
    </button>
  </div>
</div>
